/**
 * @description This manages all of the coordinates conversions
 */

import proj4 from 'proj4';
import PubSub from 'pubsub-js';
import * as THREE from 'three';
import { Box2, Vector2, Vector3 } from 'three';

import { CoordinateSystemModel, GeoidModel } from '../../../typings/api/skymap/rest/v1/.common';
import { projectReferenceAdapter } from '../react/state/project-reference-state/project-reference-adapter';
import { isValidNumber } from '../utils/math/number';
import { clampLongitudeRangeToGeoid } from '../utils/proj4/geoid';
import { getProj4Definition } from '../utils/proj4/projections';
import { isDefined } from '../utils/variables';
import { ProjectGeodataManager } from './project-geodata-manager';
import { clamp, Coordinates, Wgs84Coordinates } from './utilities/math-helpers';

/**
 * Extents array in the order minLongitude, minLatitude, maxLongitude, maxLatitude.
 * @example [10.03, 54.96, 24.17, 69.07]
 */
type ExtentsArray = [number, number, number, number];

export interface ChangedProjectionNotificationData {
  new: {
    projection: string;
    origo: Vector3;
  };
  old: {
    projection: string;
    origo: Vector3;
  };
}

/**
 * Module augmentation in order to extend the return type
 * of proj.Proj() which hides these values in its return data.
 */
declare module 'proj4' {
  interface InterfaceProjection {
    long0?: number;
    lat0?: number;
  }
}

export class CoordinateManager {
  static getInstance() {
    if (!CoordinateManager.instance) {
      CoordinateManager.instance = new CoordinateManager();
    }

    return CoordinateManager.instance;
  }
  private static instance: CoordinateManager;

  readonly projectionUpdatedEvent: string = 'onProjectionUpdated';
  private projectProjection = '';

  private origo: THREE.Vector3 | undefined;
  private orthotileOrigo?: THREE.Vector3;
  private pointCloudOrigo?: THREE.Vector3;

  private offsetDifferenceToPreviousOrigo = new Vector3();

  get OrthoTileOrigo() {
    return this.orthotileOrigo;
  }

  get PointCloudOrigo() {
    return this.pointCloudOrigo;
  }

  get origoDifference(): Vector3 {
    return this.offsetDifferenceToPreviousOrigo;
  }

  // The first origo that is set within a SkyView session
  // All asset offsets are related to this origo
  // Reset on new session
  private initialOrigo?: THREE.Vector3;

  /**
   *  Initiates the coordinate manager by re-setting the origos
   */
  initialize() {
    this.resetSceneOrigos();
    this.origo = new Vector3(0, 0, 0);
    this.initialOrigo = undefined;
  }

  reset() {
    this.resetSceneOrigos();
    this.origo = undefined;
    this.initialOrigo = undefined;
  }

  /**
   * @param projection
   * @param origo
   *  @description Set up the project information
   */
  setProjectProjectionData(projection: string, origo: THREE.Vector3): void {
    const oldProjection = this.projectProjection;
    const oldOrigo = this.origo?.clone();
    this.projectProjection = projection;
    this.origo = origo;

    PubSub.publish(this.projectionUpdatedEvent, {
      old: { projection: oldProjection, origo: oldOrigo },
      new: { projection: this.projectProjection, origo: this.origo },
    });

    this.initialOrigo = this.initialOrigo || origo;

    // Only refresh the origo diff if the new value differs from previous one.
    if (oldOrigo && !oldOrigo.equals(origo)) {
      this.offsetDifferenceToPreviousOrigo = oldOrigo.sub(origo);
    }
  }

  /**
   * @description Returns the project information
   * @returns {string}
   */
  getProjectProjection(): string {
    return this.projectProjection;
  }

  isProjectProjectionDefined(): boolean {
    return isDefined(this.projectProjection) && this.projectProjection.trim().length > 0;
  }

  hasCoordinateSystem(): boolean {
    return !!this.projectProjection && this.projectProjection !== 'local';
  }

  /**
   * @description Reset the origos based on the
   */
  resetSceneOrigos(): void {
    this.orthotileOrigo = undefined;
    this.pointCloudOrigo = undefined;
  }

  /**
   * @param origo
   * @description Set the ortho tile geographical origo
   */
  setOrthotileOrigo(origo: THREE.Vector3): void {
    if (this.orthotileOrigo === undefined) {
      this.orthotileOrigo = origo;
    }
  }

  /**
   * @param origo
   * @description Set the point cloud geographical origo
   */
  setPointCloudOrigo(origo: THREE.Vector3): void {
    if (this.pointCloudOrigo === undefined) {
      this.pointCloudOrigo = origo;
    }
  }

  /**
   *  @description Get the local origo value for the local values
   */
  getProjectionOrigo(): THREE.Vector3 | undefined {
    return this.origo?.clone();
  }

  /**
   *  @description Get the local origo value for the local values
   */
  getMAS(): number {
    return (this.origo && this.origo.z) || 0;
  }

  /**
   * @param eastingOrLongitude
   * @param northingOrLatitude
   * @param fromProj
   * @param toProj
   * @description Converts the x & y to the projects coordinates
   */
  convertCoords(
    eastingOrLongitude: number,
    northingOrLatitude: number,
    fromProj: string,
    toProj: string,
  ): THREE.Vector2 {
    // Convert the coordinates
    const projectProjection = proj4(fromProj, toProj, [eastingOrLongitude, northingOrLatitude]);
    return new THREE.Vector2(projectProjection[0], projectProjection[1]);
  }

  /**
   * Ensures that the coordinate is within the valid range for the projection.
   */
  isValidCoordinate(
    coordinate: Coordinates,
    options?: { proj4?: string; geoidBounds?: GeoidModel['header'] },
  ) {
    if (!isValidNumber(coordinate.easting) || !isValidNumber(coordinate.northing)) {
      return false;
    }

    const { min, max } = this.getProjectionRange({
      proj4: options?.proj4,
      geoidBounds: options?.geoidBounds,
    });

    const boundingBox = new Box2(
      new Vector2(min.easting, min.northing),
      new Vector2(max.easting, max.northing),
    );

    return boundingBox.containsPoint(new Vector2(coordinate.easting, coordinate.northing));
  }

  /**
   * @param x
   * @param y
   * @param fromProjection
   * @description Converts the x & y to the projects coordinates.
   */
  convertToProject(x: number, y: number, fromProjection: string): THREE.Vector2 {
    return this.convertCoords(x, y, fromProjection, this.projectProjection);
  }

  /**
   * @param coordinates
   * @param fromProjection
   * @description Converts an array of coordinates the projects projection.
   */
  convertCoordinateArrayToProject(coordinates: number[], fromProjection: string): number[] {
    return proj4(fromProjection, this.projectProjection, coordinates.concat([]));
  }

  /**
   * @param coordinates
   * @param fromProjection
   * @param toProjection
   * @description Converts an array of coordinates the target projection.
   */
  convertCoordinateArrayToProjection(
    coordinates: number[],
    fromProjection: string,
    toProjection: string,
  ): number[] {
    return proj4(fromProjection, toProjection, coordinates.concat([]));
  }

  /**
   * @param eastingOrLongitude
   * @param northingOrLatitude
   * @param toProjection
   * @description Converts the x & y to the projects coordinates.
   */
  convertFromProject(
    eastingOrLongitude: number,
    northingOrLatitude: number,
    toProjection: string,
  ): THREE.Vector2 {
    return this.convertCoords(
      eastingOrLongitude,
      northingOrLatitude,
      this.projectProjection,
      toProjection,
    );
  }

  /**
   * @param localX
   * @param localY
   * @description Converts the local x & y to the projects coordinates.
   * @returns x as easting, y as northing
   */
  localToProject(localX: number, localY: number): THREE.Vector2 {
    return new THREE.Vector2(localX + this.origo!.x, localY + this.origo!.y);
  }

  /**
   * @param x
   * @param y
   * @description Converts a world position in the viewer into projects coordinate system.
   */
  localToProjectCoords(localPosition: Vector3): Coordinates {
    const newPos = localPosition.clone().add(this.origo ?? new Vector3());
    return {
      easting: newPos.x,
      northing: newPos.y,
      height: newPos.z,
    };
  }

  /**
   * @param x
   * @param y
   * @description Converts the local x, y, z to the projects coordinates.
   */
  projectCoordsToLocal(coordinates: Coordinates): Vector3 {
    return new Vector3(coordinates.easting, coordinates.northing, coordinates.height).sub(
      this.origo ?? new Vector3(),
    );
  }

  /**
   * Converts the local x, y, z to the project scenes local coordinates.
   */
  wgs84CoordsToLocal(coordinates: Wgs84Coordinates): Vector3 {
    const { x, y } = this.convertCoords(
      coordinates.longitude,
      coordinates.latitude,
      'wgs84',
      this.projectProjection,
    );
    return new Vector3(x, y, coordinates.height).sub(this.origo ?? new Vector3());
  }

  localToWgs84Coords(localCoords: Vector3): Wgs84Coordinates {
    const adjustedCoords = localCoords.add(this.origo ?? new Vector3());
    const { x, y } = this.convertCoords(
      adjustedCoords.x,
      adjustedCoords.y,
      this.projectProjection,
      'wgs84',
    );

    // Return the coordinates in WGS84 format
    return {
      longitude: x,
      latitude: y,
      height: adjustedCoords.z,
    };
  }

  /**
   * Converts the local x, y, z to the project scenes local coordinates.
   */
  wgs84CoordsToProject(coordinates: Wgs84Coordinates, options?: { proj4?: string }): Coordinates {
    const { x, y } = this.convertCoords(
      coordinates.longitude,
      coordinates.latitude,
      'wgs84',
      options?.proj4 ?? this.projectProjection,
    );
    return { easting: x, northing: y, height: coordinates.height };
  }

  /**
   * Converts from project coordinate to wgs84 coordinates.
   * If proj4 is not provided project projection will be used.
   */
  convertToWgs84Coords(coordinates: Coordinates, options?: { proj4?: string }): Wgs84Coordinates {
    const { x, y } = this.convertCoords(
      coordinates.easting,
      coordinates.northing,
      options?.proj4 ?? this.projectProjection,
      'wgs84',
    );
    return { latitude: y, longitude: x, height: coordinates.height };
  }

  /**
   * @param easting
   * @param northing
   * @description Converts the project coordinates to local x & y.
   */
  projectToLocal(easting: number, northing: number): THREE.Vector2 {
    return new THREE.Vector2(easting - this.origo!.x, northing - this.origo!.y);
  }
  /**
   * Gets the strict boundary for the projects coordinate system.
   *
   * This is the biggest possible rectangular boundary for the projection
   * that is still within the safe ±6° longitude limit.
   *
   * NOTE: It is possible to extend this boundary further, but then we will need
   * to calculate the max and min longitude based on the target latitude instead.
   * This stems from the fact that wgs84 is a spherical coordinate system, so each
   * longitude zone is a spherical sector.
   * So for example at the north and south pole the valid easting range of the coordinate system
   * would be 0.
   *
   * Longitude accuracy:
   *
   * For Transverse Mercator projections, the accuracy diminishes significantly as you move away from the central meridian.
   * The usable longitude range for high accuracy applications is generally considered to be within ±3° to ±6° of the central meridian.
   * For broader applications where some distortion is acceptable, the range might extend to ±15° from the central meridian.
   * Beyond this, distortion becomes excessive for most practical applications.
   *
   * For UTM projections each zone is 6° wide so the accepted range would typically be ±3°.
   *
   * Latitude accuracy:
   * For projections where center latitude is defined the typically acceptable range is within ±15°
   * Projections where the center latitude is 0 are considered global typically ±80° is accepted.
   */
  getProjectionRangeOld(options?: { proj4?: string }): { min: Coordinates; max: Coordinates } {
    const customRange = resolveCustomLongLatRange();
    const { min, max, isAboveOrOnEquator } = customRange ?? this.calculateLongLatRange(options);

    const latitude = isAboveOrOnEquator ? max.latitude : min.latitude;
    const oppositeLatitude = !isAboveOrOnEquator ? max.latitude : min.latitude;

    const { easting: minEasting } = CoordinateManager.getInstance().wgs84CoordsToProject(
      {
        latitude,
        longitude: min.longitude,
        height: 0,
      },
      options,
    );
    const { northing: northing, easting: maxEasting } =
      CoordinateManager.getInstance().wgs84CoordsToProject(
        {
          latitude,
          longitude: max.longitude,
          height: 0,
        },
        options,
      );

    // Calculate the opposite northing.
    const { northing: oppositeNorthing } = CoordinateManager.getInstance().wgs84CoordsToProject(
      {
        latitude: oppositeLatitude,
        longitude: max.longitude,
        height: 0,
      },
      options,
    );

    return {
      min: {
        easting: minEasting,
        northing: Math.min(northing, oppositeNorthing),
        height: Number.MIN_SAFE_INTEGER,
      },
      max: {
        easting: maxEasting,
        northing: Math.max(northing, oppositeNorthing),
        height: Number.MAX_SAFE_INTEGER,
      },
    };
  }

  /**
   * Gets the optimistic boundary for the projects coordinate system.
   *
   * This is the smallest possible rectangular boundary for the projection that
   * covers the complete projected area.
   *
   * This means that the corners or the map will generally be outside of the acceptable
   * boundary of the coordinate system. Ideally we should limit editing in these areas or
   * limit the camera from going to these areas if possible.
   *
   * Longitude accuracy:
   *
   * For Transverse Mercator projections, the accuracy diminishes significantly as you move away from the central meridian.
   * The usable longitude range for high accuracy applications is generally considered to be within ±3° to ±6° of the central meridian.
   * For broader applications where some distortion is acceptable, the range might extend to ±15° from the central meridian.
   * Beyond this, distortion becomes excessive for most practical applications.
   *
   * For UTM projections each zone is 6° wide so the accepted range would typically be ±3°.
   *
   * Latitude accuracy:
   * For projections where center latitude is defined the typically acceptable range is within ±15°
   * Projections where the center latitude is 0 are considered global typically ±80° is accepted.
   */
  getProjectionRange(options?: { proj4?: string; geoidBounds?: GeoidModel['header'] }): {
    min: Coordinates;
    max: Coordinates;
  } {
    const customRange = resolveCustomLongLatRange();
    const { min, max, isAboveOrOnEquator } = customRange ?? this.calculateLongLatRange(options);

    const geoidMinLatitude = options?.geoidBounds?.lat.min;
    const geoidMaxLatitude = options?.geoidBounds?.lat.max;

    // Clamp longitude and latitude ranges to geoid bounds if they are more restrictive
    if (isDefined(options?.geoidBounds)) {
      ({ min: min.longitude, max: max.longitude } = clampLongitudeRangeToGeoid(
        options.geoidBounds,
        min.longitude,
        max.longitude,
      ));
    }
    if (isDefined(geoidMinLatitude)) {
      min.latitude = Math.max(min.latitude, geoidMinLatitude);
    }
    if (isDefined(geoidMaxLatitude)) {
      max.latitude = Math.min(max.latitude, geoidMaxLatitude);
    }

    const latitude = isAboveOrOnEquator ? max.latitude : min.latitude;
    const oppositeLatitude = !isAboveOrOnEquator ? max.latitude : min.latitude;

    const centerLongitude = THREE.MathUtils.lerp(min.longitude, max.longitude, 0.5);

    /**
     * On the north hemisphere the highest easting value will be found on the bottom right side of the map.
     * On the south hemisphere the highest easting value will be found on the top right side of the map.
     */
    const { easting: minEasting } = CoordinateManager.getInstance().wgs84CoordsToProject(
      {
        latitude: oppositeLatitude,
        longitude: min.longitude,
        height: 0,
      },
      options,
    );

    /**
     * On the north hemisphere the highest easting value will be found on the bottom left side of the map.
     * On the south hemisphere the highest easting value will be found on the top left side of the map.
     */
    const { easting: maxEasting } = CoordinateManager.getInstance().wgs84CoordsToProject(
      {
        latitude: oppositeLatitude,
        longitude: max.longitude,
        height: 0,
      },
      options,
    );

    /**
     * The largest latitude value will be on the center of the map so we need to calculate
     * the northing using the max or min longitude.
     */
    const { northing: northing } = CoordinateManager.getInstance().wgs84CoordsToProject(
      {
        latitude,
        longitude: max.longitude,
        height: 0,
      },
      options,
    );

    /**
     * On the north hemisphere the smallest latitude value will be on the center bottom of the map.
     * On the south hemisphere the highest latitude value will be on the top center of the map.
     */
    const { northing: oppositeNorthing } = CoordinateManager.getInstance().wgs84CoordsToProject(
      {
        latitude: oppositeLatitude,
        longitude: centerLongitude,
        height: 0,
      },
      options,
    );

    return {
      min: {
        easting: Math.min(minEasting, maxEasting),
        northing: Math.min(northing, oppositeNorthing),
        height: Number.MIN_SAFE_INTEGER,
      },
      max: {
        easting: Math.max(minEasting, maxEasting),
        northing: Math.max(northing, oppositeNorthing),
        height: Number.MAX_SAFE_INTEGER,
      },
    };
  }

  private calculateLongLatRange(options?: { proj4?: string }): {
    min: Wgs84Coordinates;
    max: Wgs84Coordinates;
    isAboveOrOnEquator: boolean;
  } {
    const { latitude: centralLatitude, longitude: centralMeridian } =
      CoordinateManager.getProj4Center(options?.proj4 ?? this.projectProjection);

    // Calculate the effective longitude range for high accuracy.
    // Range the longitude between ±6° of the central meridian.
    const minLongitude = (centralMeridian ?? 0) - 6;
    const maxLongitude = (centralMeridian ?? 0) + 6;

    // This determines the hard limit for max and min latitude.
    const latitudeRange = getLatitudeRange(options?.proj4);

    // Unless central latitude is 0 we limit the latitude range to ±15°.
    const minLatitude = Math.max(
      latitudeRange.min,
      centralLatitude === 0 ? latitudeRange.min : centralLatitude - 15,
    );
    const maxLatitude = Math.min(
      latitudeRange.max,
      centralLatitude === 0 ? latitudeRange.max : centralLatitude + 15,
    );

    /**
     * Convert the corners of the valid the longitude/latitude-zone into project coordinates
     * to get the biggest rectangular boundary possible for the coordinate system.
     *
     * Here we need to make sure to use same latitude for calculating min and max easting value.
     * Otherwise the bounding box will become skewed for non central coordinate systems.
     *
     * This is necessary because when converting from WGS84 to projected coordinates
     * the easting range gets wider the closer we get to the equator.
     */
    const isAboveOrOnEquator = centralLatitude >= 0;

    return getTimelineCenteredRange(
      [minLongitude, minLatitude, maxLongitude, maxLatitude],
      isAboveOrOnEquator,
    );
  }

  static getProj4Center(proj4Definition?: string): Wgs84Coordinates {
    // Parse the proj4 string to get projection parameters.
    const projectionDefinition = proj4.Proj(proj4Definition);

    // Extract the central meridian (lon_0).
    // Round because calculation with PI regularly generate very long decimals.
    // NOTE: Implementation assumes long0 and lat 0 exists but there may be cases where they are
    // undefined. We haven't encountered such projections yet so we keep it like this for now.
    return {
      longitude: parseFloat((projectionDefinition.long0! * (180 / Math.PI)).toFixed(5)),
      latitude: parseFloat((projectionDefinition.lat0! * (180 / Math.PI)).toFixed(5)),
      height: 0,
    };
  }

  /**
   * Gets the default origo for the coordinate system.
   * Note this is not the same as the center of the coordinate system.
   * Will default to local coordinates if coordinate system is undefined.
   */
  getCoordinateSystemDefaultOrigo(coordinateSystem?: CoordinateSystemModel): Vector3 {
    const proj4Definition = coordinateSystem?.proj4 ?? 'local';
    const center = CoordinateManager.getProj4Center(proj4Definition);
    center.latitude = resolveCustomCentralLatitude(center.latitude, coordinateSystem?.name);

    const coordinates = this.wgs84CoordsToProject(
      {
        longitude: center.longitude,
        latitude: center.latitude,
        height: 0,
      },
      { proj4: proj4Definition },
    );

    return new Vector3(coordinates.easting, coordinates.northing, coordinates.height);
  }

  getLocalRange(): { min: Vector3; max: Vector3 } {
    const { min, max } = this.getProjectionRange();
    return {
      min: this.projectCoordsToLocal(min),
      max: this.projectCoordsToLocal(max),
    };
  }
}

/**
 * Resolves a custom latitude for global coordinate systems (i.e. coordinate system where lat_0=0)
 * This is mainly because we don't want the viewer to center on Congo or in the middle of the ocean
 * when opening projects in the CAD-viewer mode.
 *
 * In the future we may consider moving custom origo to the database but for now we cover the most common ones.
 */
function resolveCustomCentralLatitude(latitude: number, coordinateSystemName?: string) {
  if (latitude !== 0) {
    return latitude;
  }

  if (isSweref99CoordinateSystem(coordinateSystemName)) {
    return 57; // Aligns the latitude with the south of Sweden.
  }

  if (isGkFinCoordinateSystem(coordinateSystemName)) {
    return 63; // Aligns the latitude with Finland.
  }

  if (isEtrs89CoordinateSystem(coordinateSystemName)) {
    return 53; // Aligns the latitude with the north of Europe.
  }

  if (isDhdnCoordinateSystem(coordinateSystemName)) {
    return 51; // Aligns the latitude with the center of Germany.
  }

  return latitude;
}

/**
 * In order to support a wider longitude range for coordinate systems we limit the max and
 * min latitude respectively.
 */
function getLatitudeRange(projection?: string) {
  /**
   * Covers all of the Scandinavian peninsula, Iceland, Alaska for example but does not include
   * Svalbard, Greenland or the North Pole.
   */
  const maxLatitude = 80;

  /**
   * Covers all of South America, New Zealand and Australia.
   */
  const minLatitude = -80;

  const name = isDefined(projection)
    ? projection
    : projectReferenceAdapter.getCoordinateSystemOrDefault().name;

  return {
    max: resolveCustomMaxLatitude(name) ?? maxLatitude,
    min: resolveCustomMinLatitude(name) ?? minLatitude,
  };
}

/**
 * Resolves a custom max latitude for projections with matching name.
 */
function resolveCustomMaxLatitude(coordinateSystemName?: string) {
  if (isSweref99CoordinateSystem(coordinateSystemName)) {
    return 69.07; // Top of Sweden.
  }

  if (isDhdnCoordinateSystem(coordinateSystemName)) {
    return 55.47; // Top of Germany.
  }
}

/**
 * Resolves a custom min latitude for projections with matching name.
 */
function resolveCustomMinLatitude(coordinateSystemName?: string) {
  if (isSweref99CoordinateSystem(coordinateSystemName)) {
    return 54; // Bottom of Sweden.
  }

  if (isGkFinCoordinateSystem(coordinateSystemName)) {
    return 59; // Bottom of Finland.
  }

  if (isDhdnCoordinateSystem(coordinateSystemName)) {
    return 47; // Bottom of Germany.
  }

  if (isEtrs89CoordinateSystem(coordinateSystemName)) {
    return 30; // Bottom of Europe.
  }
}

/**
 * Used by many European countries such as Norway, Germany and Finland.
 */
function isEtrs89CoordinateSystem(coordinateSystemName?: string) {
  return coordinateSystemName?.toUpperCase().includes('ETRS89') ?? false;
}

/**
 * Used only in Germany.
 */
function isDhdnCoordinateSystem(coordinateSystemName?: string) {
  return coordinateSystemName?.toUpperCase().includes('DHDN') ?? false;
}

/**
 * Used only in Sweden.
 */
export function isSweref99CoordinateSystem(coordinateSystemName?: string) {
  return coordinateSystemName?.toUpperCase().includes('SWEREF99') ?? false;
}

/**
 * Used only in Finland.
 */
function isGkFinCoordinateSystem(coordinateSystemName?: string) {
  return (
    (coordinateSystemName?.toUpperCase().includes('GK') &&
      coordinateSystemName?.toUpperCase().includes('FIN')) ??
    false
  );
}

/**
 * Resolves a custom WGS 84 range for the projection.
 *
 * By using a custom bounding box we are able to expand the area in which the camera can move.
 * However the camera will still be restricted to the maximal projected area for these boundaries.
 */
function resolveCustomLongLatRange(
  projection?: string,
): { min: Wgs84Coordinates; max: Wgs84Coordinates; isAboveOrOnEquator: boolean } | undefined {
  const coordinateSystem = projectReferenceAdapter.getCoordinateSystemOrDefault();

  // No custom range if coordinate system is not set for the project.
  if (!isDefined(coordinateSystem)) {
    return {
      isAboveOrOnEquator: true,
      max: {
        latitude: 60,
        longitude: 120,
        height: Number.MAX_SAFE_INTEGER,
      },
      min: {
        latitude: -60,
        longitude: -120,
        height: Number.MIN_SAFE_INTEGER,
      },
    };
  }

  const definition = projection ? getProj4Definition(projection) : undefined;

  // No custom range if projections do not match.
  if (isDefined(definition) && definition !== coordinateSystem.proj4) {
    return undefined;
  }

  // Every boundary has a custom range which can be found on epsg.io.
  // Ideally we should save this boundary in DB so we don't need to
  // make a new deploy if we want to expand on this list.
  switch (coordinateSystem.authority) {
    case 'EPSG::3006': // Sweref99 TM.
      return getTimelineCenteredRange([10.03, 54.96, 24.17, 69.07], true);
    case 'EPSG::3067': // TM35FIN(E,N) -- Finland.
      return getTimelineCenteredRange([19.08, 58.84, 31.59, 70.09], true);
    case 'EPSG::4647': // UTM zone 32N (zE-N) (Actually a tmerc projection).
      return getTimelineCenteredRange([6.0, 47.27, 12.0, 55.47], true);
    case 'EPSG::25833': //  UTM zone 33N (Actually a tmerc projection).
      return getTimelineCenteredRange([12.0, 46.4, 18.01, 69.8], true);
    default:
      return undefined;
  }
}

function getBoundary(extents: ExtentsArray, aboveEquator: boolean) {
  return {
    min: {
      longitude: extents[0],
      latitude: extents[1],
      height: Number.MIN_SAFE_INTEGER,
    },
    max: {
      longitude: extents[2],
      latitude: extents[3],
      height: Number.MAX_SAFE_INTEGER,
    },
    isAboveOrOnEquator: aboveEquator,
  };
}

/**
 * "Expand" the longitude range by limiting the latitude range around the origo.
 * Note that if there is no active timeline then the boundary will remain unaltered.
 */
function getTimelineCenteredRange(extents: ExtentsArray, isAboveOrOnEquator = true) {
  const boundary = getBoundary(extents, isAboveOrOnEquator);

  const origo = ProjectGeodataManager.instance().hasTimelineGeodata()
    ? CoordinateManager.getInstance().getProjectionOrigo()
    : undefined;

  if (origo) {
    const wgs84Position = CoordinateManager.getInstance().convertToWgs84Coords({
      northing: origo.y,
      easting: origo.x,
      height: 0,
    });

    const range = 3;
    const clampedLatitude = clamp(
      wgs84Position.latitude,
      boundary.min.latitude + range,
      boundary.max.latitude - range,
    );

    boundary.min.latitude = clampedLatitude - range;
    boundary.max.latitude = clampedLatitude + range;
  }

  return boundary;
}
